"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mergeAndDeduplicateAttributes = mergeAndDeduplicateAttributes;
exports.buildApplyPayload = buildApplyPayload;
exports.isAttributesFilterActive = isAttributesFilterActive;
exports.isFilterDirty = isFilterDirty;
exports.groupSelectedValuesByAttribute = groupSelectedValuesByAttribute;
const ramda_1 = require("ramda");
const getAttributeFromValueKey = (key) => {
    const sepIdx = key.indexOf('::');
    return sepIdx === -1 ? null : key.slice(0, sepIdx);
};
const getSelectedAttributes = (labels, selectedValues, deselectedValues) => {
    const attributes = new Set(labels);
    [...selectedValues, ...deselectedValues].forEach((key) => {
        const attribute = getAttributeFromValueKey(key);
        if (attribute) {
            attributes.add(attribute);
        }
    });
    return Array.from(attributes);
};
/**
 * Converts text attribute edges to ProductAttribute[], deduplicates by label (first wins),
 * then prepends converted measure edges.
 */
function mergeAndDeduplicateAttributes(labelEdges, valueEdges, measureEdges) {
    var _a, _b, _c;
    const measures = (_a = measureEdges === null || measureEdges === void 0 ? void 0 : measureEdges.map(({ node: { dimension, productCount } }) => ({ label: dimension, productCount, isMeasure: true }))) !== null && _a !== void 0 ? _a : [];
    const mapTextEdge = ({ node: { label, productCount, productLabelValidations } }) => {
        const isModified = (productLabelValidations === null || productLabelValidations === void 0 ? void 0 : productLabelValidations.totalCount) === 0;
        return {
            label, productCount, isValid: !isModified, isModified,
        };
    };
    const convertedLabels = (_b = labelEdges === null || labelEdges === void 0 ? void 0 : labelEdges.map(mapTextEdge)) !== null && _b !== void 0 ? _b : [];
    const convertedValues = (_c = valueEdges === null || valueEdges === void 0 ? void 0 : valueEdges.map(mapTextEdge)) !== null && _c !== void 0 ? _c : [];
    const labelsSet = new Set();
    const deduplicated = [];
    for (const attr of [...convertedLabels, ...convertedValues]) {
        if (!labelsSet.has(attr.label)) {
            labelsSet.add(attr.label);
            deduplicated.push(attr);
        }
    }
    return measures.concat(deduplicated);
}
/**
 * Builds the Redux dispatch payload for the Apply action in the filter popover.
 * Uses dual-mode pattern: when isAllSelected, labels/values are excluded items;
 * otherwise they are selected items.
 */
function buildApplyPayload(isAllSelected, labels, values, deselectedValues = [], searchText = '') {
    if (isAllSelected) {
        return {
            selectedAttributes: 'ALL',
            deselectedAttributes: labels,
            selectedValues: [],
            deselectedValues: values,
            searchText,
        };
    }
    const selectedAttributes = getSelectedAttributes(labels, values, deselectedValues);
    if (selectedAttributes.length === 0 && values.length === 0 && deselectedValues.length === 0) {
        return {
            selectedAttributes: null,
            deselectedAttributes: [],
            selectedValues: [],
            deselectedValues: [],
            searchText,
        };
    }
    return {
        selectedAttributes,
        deselectedAttributes: [],
        selectedValues: values,
        deselectedValues,
        searchText,
    };
}
function isAttributesFilterActive(filter) {
    if (filter.selectedAttributes === 'ALL') {
        return true;
    }
    if (Array.isArray(filter.selectedAttributes) && filter.selectedAttributes.length > 0) {
        return true;
    }
    return filter.deselectedAttributes.length > 0
        || filter.selectedValues.length > 0
        || filter.deselectedValues.length > 0;
}
const sameItems = (a = [], b = []) => a.length === b.length && (0, ramda_1.isEmpty)((0, ramda_1.symmetricDifference)(a, b));
const sameSelectedAttributes = (a, b) => (a === 'ALL' || b === 'ALL' ? a === b : sameItems(a !== null && a !== void 0 ? a : [], b !== null && b !== void 0 ? b : []));
/**
 * Determines whether the in-popover draft selection differs from the currently
 * applied filter state. Drives the Apply button's enabled state so that:
 * - Reopening the popover without changes keeps Apply disabled.
 * - Any change (including clearing a previously saved selection) enables Apply.
 */
function isFilterDirty(draft, applied) {
    return !(sameSelectedAttributes(draft.selectedAttributes, applied.selectedAttributes)
        && sameItems(draft.deselectedAttributes, applied.deselectedAttributes)
        && sameItems(draft.selectedValues, applied.selectedValues)
        && sameItems(draft.deselectedValues, applied.deselectedValues)
        && draft.searchText === applied.searchText);
}
/**
 * Groups "attribute::value" keys into a map { attribute: [values] }.
 * Attributes without values get an empty array.
 */
function groupSelectedValuesByAttribute(selectedAttributes, selectedValues) {
    const valuesByAttribute = {};
    selectedAttributes.forEach((attr) => { valuesByAttribute[attr] = []; });
    selectedValues === null || selectedValues === void 0 ? void 0 : selectedValues.forEach((key) => {
        const sepIdx = key.indexOf('::');
        if (sepIdx === -1)
            return;
        const attr = key.slice(0, sepIdx);
        const val = key.slice(sepIdx + 2);
        if (valuesByAttribute[attr]) {
            valuesByAttribute[attr].push(val);
        }
    });
    return valuesByAttribute;
}
